import React, { useEffect, useState } from "react";
import { X, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SHOP_COLORS, OCCASIONS, FABRICS, adminUploadImage } from "../../lib/api";

const CATEGORY_OPTIONS = [
  { value: "traditional", label: "Traditional" },
  { value: "cotton", label: "Cotton" },
  { value: "silk", label: "Silk" },
  { value: "patteda-anchu", label: "Patteda Anchu" },
  { value: "daily", label: "Daily Wear" },
  { value: "premium", label: "Premium" },
];

const EMPTY = {
  name: "", price: "", original_price: "", category: "traditional", fabric: "Handloom Cotton",
  colors: [], occasions: [], images: ["", "", "", ""], description: "",
  is_new: false, is_bestseller: false, in_stock: true, stock_qty: "",
};

export const ProductForm = ({ open, onOpenChange, product, onSave, saving }) => {
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(null);

  const handleFile = async (i, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/jpg"].includes(file.type)) {
      return toast.error("Please choose a JPG photo");
    }
    setUploading(i);
    try {
      const res = await adminUploadImage(file);
      set("images", form.images.map((x, j) => (j === i ? res.url : x)));
    } catch {
      toast.error("Photo upload failed. Please try again.");
    } finally {
      setUploading(null);
    }
  };

  useEffect(() => {
    if (open) {
      if (product) {
        setForm({
          name: product.name, price: String(product.price),
          original_price: product.original_price ? String(product.original_price) : "",
          category: product.category, fabric: product.fabric,
          colors: product.colors || [], occasions: product.occasions || [],
          images: [...(product.images || []), "", "", "", ""].slice(0, 4),
          description: product.description || "",
          is_new: !!product.is_new, is_bestseller: !!product.is_bestseller, in_stock: !!product.in_stock,
          stock_qty: product.stock_qty != null ? String(product.stock_qty) : "",
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, product]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (key, value) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value] }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      name: form.name.trim(),
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      category: form.category,
      fabric: form.fabric,
      colors: form.colors,
      occasions: form.occasions,
      images: form.images.filter((u) => u.trim()),
      description: form.description.trim(),
      is_new: form.is_new,
      is_bestseller: form.is_bestseller,
      in_stock: form.in_stock,
      stock_qty: form.stock_qty !== "" ? parseInt(form.stock_qty, 10) : null,
    });
  };

  const valid = form.name.trim() && parseFloat(form.price) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ivory border-gold/40 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="product-form-modal">
        <DialogTitle className="sr-only">{product ? "Edit Saree" : "Add Saree"}</DialogTitle>
        <div className="flex items-center justify-between p-6 border-b border-gold/25">
          <h2 className="font-heading text-2xl text-charcoal">{product ? "Edit Saree" : "Add New Saree"}</h2>
          <button onClick={() => onOpenChange(false)} aria-label="Close"><X className="w-5 h-5 text-charcoal/50" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Saree Name *</label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" className="mt-2 bg-ivory border-gold/40 rounded-sm h-11" placeholder="e.g. Gauri Traditional Ilkal" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Price (₹) *</label>
              <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" className="mt-2 bg-ivory border-gold/40 rounded-sm h-11" placeholder="4850" />
            </div>
            <div>
              <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Original Price (₹)</label>
              <Input type="number" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} data-testid="pf-original-price" className="mt-2 bg-ivory border-gold/40 rounded-sm h-11" placeholder="6200 (optional)" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Collection</label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-2 border-gold/40 rounded-sm h-11" data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ivory border-gold/40 rounded-sm">
                  {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`pf-category-${c.value}`}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Fabric</label>
              <Select value={form.fabric} onValueChange={(v) => set("fabric", v)}>
                <SelectTrigger className="mt-2 border-gold/40 rounded-sm h-11" data-testid="pf-fabric"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ivory border-gold/40 rounded-sm">
                  {FABRICS.map((f) => <SelectItem key={f} value={f} data-testid={`pf-fabric-${f.toLowerCase().replace(/\s+/g, "-")}`}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Colours</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SHOP_COLORS.map((c) => (
                <button
                  type="button"
                  key={c.name}
                  onClick={() => toggleIn("colors", c.name)}
                  data-testid={`pf-color-${c.name.toLowerCase()}`}
                  title={c.name}
                  className={`w-8 h-8 rounded-full border transition-all ${form.colors.includes(c.name) ? "ring-2 ring-maroon ring-offset-2 ring-offset-ivory" : "border-gold/40"}`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Occasions</label>
            <div className="flex flex-wrap gap-3 mt-2">
              {OCCASIONS.map((o) => (
                <label key={o} className="flex items-center gap-2 text-sm text-charcoal/70 cursor-pointer">
                  <Checkbox checked={form.occasions.includes(o)} onCheckedChange={() => toggleIn("occasions", o)} data-testid={`pf-occasion-${o.toLowerCase().replace(/\s+/g, "-")}`} className="border-gold/50 data-[state=checked]:bg-maroon data-[state=checked]:border-maroon" />
                  {o}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Stock Quantity (empty = unlimited)</label>
            <Input type="number" min="0" value={form.stock_qty} onChange={(e) => set("stock_qty", e.target.value)} data-testid="pf-stock" className="mt-2 bg-ivory border-gold/40 rounded-sm h-11" placeholder="e.g. 5" />
            <p className="text-[11px] text-charcoal/40 mt-1.5">Each order reduces this automatically. At 0, the saree shows as Out of Stock.</p>
          </div>

          <div>
            <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Photos — JPG (up to 4)</label>
            <div className="grid grid-cols-4 gap-3 mt-2">
              {form.images.map((u, i) => (
                <div key={i} className="relative aspect-[3/4] bg-surface border border-gold/30 overflow-hidden" data-testid={`pf-photo-slot-${i}`}>
                  {u ? (
                    <>
                      <img src={u} alt={`Saree photo ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => set("images", form.images.map((x, j) => (j === i ? "" : x)))}
                        data-testid={`pf-photo-remove-${i}`}
                        aria-label="Remove photo"
                        className="absolute top-1 right-1 w-6 h-6 bg-charcoal/70 text-ivory rounded-full flex items-center justify-center hover:bg-maroon"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <label className="w-full h-full flex flex-col items-center justify-center gap-1.5 cursor-pointer text-charcoal/50 hover:text-maroon transition-colors">
                      {uploading === i ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" strokeWidth={1.5} />}
                      <span className="text-[10px] tracking-wider uppercase">{uploading === i ? "Uploading…" : "Upload JPG"}</span>
                      <input type="file" accept="image/jpeg,image/jpg" className="hidden" data-testid={`pf-file-${i}`} onChange={(e) => handleFile(i, e)} />
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">Description</label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="pf-description" className="mt-2 bg-ivory border-gold/40 rounded-sm" placeholder="Short story of this saree…" />
          </div>

          <div className="flex flex-wrap gap-5">
            {[["is_new", "New Arrival"], ["is_bestseller", "Best Seller"], ["in_stock", "In Stock"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-charcoal/70 cursor-pointer">
                <Checkbox checked={form[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`pf-${k.replace("_", "-")}`} className="border-gold/50 data-[state=checked]:bg-maroon data-[state=checked]:border-maroon" />
                {label}
              </label>
            ))}
          </div>

          <button
            type="submit"
            disabled={!valid || saving}
            data-testid="pf-save-btn"
            className="w-full bg-maroon text-ivory text-xs tracking-[0.18em] uppercase py-4 hover:bg-maroon-dark transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : product ? "Save Changes" : "Add Saree"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
